import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { duration } from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import tinycolor from 'tinycolor2';

import { TimeLeft } from '@webapp/common/components/time-left';
import { useElementSize } from '@webapp/common/hooks/use-element-size';
import { MARGIN_BASE_UNIT, roundFloat } from '@webapp/common/lib/ui';
import { IndicatorPosition } from '@webapp/common/resources/survey';

import { selectInfo, selectTimerRef } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { SurveyFooter } from '../footer';

import css from './indicator.css';

export const SurveyIndicator: VFC<{ hideFooter: boolean }> = ({ hideFooter }) => {
    const {
        design: {
            fonts: { elementColor: color },
            view: { paddingLeft, paddingRight },
        },
        params: {
            alert: { indicator: indicatorLabel },
            other: { indicator, indicatorPosition },
        },
    } = useStateSelector(selectInfo);
    const timerRef = useStateSelector(selectTimerRef);
    const timeLeft = useSelector(timerRef, (s) =>
        s ? duration(Math.max(0, s.context.duration * 1000 - s.context.timeAgo)) : null,
    );

    const invalidQuestionsQuantity = useStateSelector((s) => s.invalidQuestionsQuantity);
    const progress = useStateSelector((s) => s.progress);

    const width = indicator ? progress : null;
    const ref = useRef(null);
    const { height } = useElementSize(ref);
    const inPercents = useMemo(() => (typeof width === 'number' ? width * 100 : null), [width]);
    const percentIsNumber = typeof inPercents === 'number';
    const offsets = useMemo(
        () => ({
            left: (paddingLeft || 0) * parseInt(MARGIN_BASE_UNIT),
            right: (paddingRight || 0) * parseInt(MARGIN_BASE_UNIT),
        }),
        [paddingLeft, paddingRight],
    );

    const lightColor = useMemo(() => {
        if (!color) return;

        return tinycolor(color).setAlpha(0.2).toRgbString();
    }, [color]);

    const colorStyle = useMemo(() => ({ color }), [color]);
    const style1 = useMemo(() => ({ backgroundColor: lightColor }), [lightColor]);
    const style2 = useMemo(
        () => ({ width: `${inPercents ? roundFloat(inPercents) : 0}%`, backgroundColor: color }),
        [color, inPercents],
    );

    const indicatorStyle = useMemo(
        () => ({
            marginLeft: `${offsets?.left / 2 + 3}%`,
            marginRight: `${offsets?.right / 2 + 3}%`,
            paddingLeft: `${offsets?.left / 2 + 1.5}%`,
            paddingRight: `${offsets?.left / 2 + 1.5}%`,
            marginTop: ref.current && indicatorPosition === IndicatorPosition.TOP ? `${-1 * height}px` : undefined,
        }),
        [offsets?.left, offsets?.right],
    );

    useEffect(() => {
        const nodeRef = ref.current;
        if (nodeRef) {
            const headerResizeObserver = new ResizeObserver(() => {
                document.body?.style?.setProperty(
                    '--survey-footer-size',
                    `calc(${nodeRef.clientHeight}px + env(safe-area-inset-bottom, 0))`,
                );
            });

            headerResizeObserver.observe(nodeRef);

            return () => headerResizeObserver.unobserve(nodeRef);
        }
    }, [ref]);

    return (
        <div
            ref={ref}
            style={indicatorStyle}
            className={cn(css.indicatorBlock, {
                [css.invalid]: invalidQuestionsQuantity,
                [css.withoutIndicator]: inPercents === null,
                [css.posTop]: indicatorPosition === IndicatorPosition.TOP,
            })}
        >
            <div className={css.top}>
                {percentIsNumber && (
                    <div className={css.inNumbers} style={colorStyle}>
                        {`${inPercents ? inPercents.toFixed(0) : 0} / 100 %`}
                    </div>
                )}
                {!!invalidQuestionsQuantity && (
                    <div className={css.invalidQuestions}>{`${indicatorLabel} ${invalidQuestionsQuantity}`}</div>
                )}
                {timeLeft && <TimeLeft className={css.time} color={color} value={timeLeft} />}
                {indicatorPosition === IndicatorPosition.BOTTOM && !hideFooter && (
                    <SurveyFooter withIndicator className={css.footerLogo} />
                )}
            </div>
            {percentIsNumber && (
                <div className={css.indicator} style={style1}>
                    <div className={css.filledIndicator} style={style2} />
                </div>
            )}
        </div>
    );
};
SurveyIndicator.displayName = 'SurveyIndicator';
